import React from 'react';

import type { FormFieldDef } from '../types';

/**
 * Opt-in seam for a host that draws the form's sections and controls in its own design.
 *
 * The engine still decides everything about the form: which sections and questions show, what a valid answer is,
 * the values, and the errors. A renderer only decides how a section that shows, or a question that shows, is drawn,
 * and is handed what the engine has already worked out for it.
 *
 * With no provider installed (every form but the broker's step 7 and the Risk Data questionnaire), `Section` and
 * `FieldControl` render exactly what they rendered before. No renderer is called, and no node is added.
 */
export interface SectionRenderArgs {
  readonly title?: string;
  /** A section inside another. */
  readonly isSubsection: boolean;
  /** Every field the section contains, its subsections' included, in render order. */
  readonly fieldIds: readonly string[];
  /** The section's controls from `SectionDraftContext`, when an overlay installs one. */
  readonly actions?: React.ReactNode;
  /** The section's elements, as the engine renders them. */
  readonly children: React.ReactNode;
}

export interface FieldControlRenderArgs {
  readonly meta: FormFieldDef;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  /** The field's first error message, or empty. */
  readonly error: string;
  /** Whether the engine requires an answer, as it marks one with an asterisk. */
  readonly isRequired: boolean;
  readonly disabled: boolean;
  /**
   * The engine's confidence chip for the field, present only when the form shows confidence. A host draws it where it
   * wants it, or ignores it; with no host renderer the engine draws the chip in the label as before.
   */
  readonly confidence?: React.ReactNode;
  /** The engine's own control for the field, without its label: for a field the host doesn't draw itself. */
  readonly control: React.ReactNode;
}

export interface FormRenderers {
  readonly section?: (args: SectionRenderArgs) => React.ReactNode;
  readonly fieldControl?: (args: FieldControlRenderArgs) => React.ReactNode;
}

const FormRenderersContext = React.createContext<FormRenderers | null>(null);

export const FormRenderersProvider = FormRenderersContext.Provider;

export const useFormRenderers = (): FormRenderers | null =>
  React.useContext(FormRenderersContext);
